import { useSeoMeta } from '@unhead/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useSpaces } from '@/hooks/useSpaces';
import { LoginArea } from '@/components/auth/LoginArea';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, FileText, ArrowUpRight, GitBranch } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { CreateSpaceDialog } from '@/components/spaces/CreateSpaceDialog';
import { getTagValue, parsePageTags } from '@/lib/stele';
import { nip19 } from 'nostr-tools';
import { cn } from '@/lib/utils';

const Index = () => {
  const { user, metadata } = useCurrentUser();
  const { data: spaces, isLoading } = useSpaces(user?.pubkey);
  const navigate = useNavigate();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useSeoMeta({
    title: 'Stele — Sovereign Documentation',
    description: 'Nostr-native documentation. Your keys, your relays, your content.',
  });

  return (
    <div className="min-h-screen bg-background">

      {/* ── Header ── */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-6">
          <a href="/" className="flex items-center gap-2.5 group">
            <GitBranch className="h-4 w-4 text-foreground/80 group-hover:text-foreground transition-colors" />
            <span className="font-mono text-sm font-semibold tracking-widest text-foreground uppercase select-none">
              STELE
            </span>
          </a>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <LoginArea className="max-w-60" />
          </div>
        </div>
      </header>

      {/* ── Main ── */}
      <main className="mx-auto max-w-5xl px-6">

        {!user ? (
          /* ── Logged out: hero ── */
          <div className="flex flex-col items-start justify-center py-28">
            <p className="text-xs font-mono tracking-[0.2em] text-muted-foreground uppercase mb-8">
              Sovereign Documentation
            </p>
            <h1 className="text-[clamp(2.5rem,6vw,4.5rem)] font-bold tracking-[-0.04em] leading-[1.05] text-foreground mb-6 max-w-2xl">
              Documentation that belongs to you.
            </h1>
            <p className="text-muted-foreground text-lg max-w-lg leading-relaxed mb-10">
              Write, publish, and share documentation as signed Nostr events.
              No accounts. No servers. No lock-in.
            </p>
            <LoginArea className="max-w-60" />

            {/* Feature list */}
            <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-px border border-border w-full">
              {[
                { label: 'Self-sovereign', body: 'Your npub is your identity. Sign with any Nostr extension.' },
                { label: 'Relay-native', body: 'Lives on your relays. Readable by anyone with an internet connection.' },
                { label: 'Portable', body: 'Export any space as Markdown in one click. Always yours.' },
              ].map((f) => (
                <div key={f.label} className="bg-card p-6">
                  <p className="text-xs font-mono tracking-[0.15em] text-muted-foreground uppercase mb-3">{f.label}</p>
                  <p className="text-sm text-foreground/80 leading-relaxed">{f.body}</p>
                </div>
              ))}
            </div>
          </div>

        ) : (
          /* ── Logged in: spaces ── */
          <div className="py-12">
            <div className="flex items-end justify-between mb-10 border-b border-border pb-6">
              <div>
                <p className="text-xs font-mono tracking-[0.2em] text-muted-foreground uppercase mb-2">
                  Your Spaces
                </p>
                <h1 className="text-2xl font-bold tracking-[-0.03em]">
                  {metadata?.name ? `${metadata.name}'s spaces` : 'Documentation spaces'}
                </h1>
              </div>
              <Button
                onClick={() => setCreateDialogOpen(true)}
                size="sm"
                className="gap-2 text-xs tracking-wide uppercase font-mono h-8 px-4"
              >
                <Plus className="h-3.5 w-3.5" />
                New Space
              </Button>
            </div>

            {isLoading ? (
              /* Skeleton grid */
              <div className="grid gap-px sm:grid-cols-2 lg:grid-cols-3 border border-border">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-card p-6 space-y-3">
                    <Skeleton className="h-4 w-8 rounded-sm" />
                    <Skeleton className="h-5 w-40" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                ))}
              </div>

            ) : spaces && spaces.length > 0 ? (
              <div className="grid gap-px sm:grid-cols-2 lg:grid-cols-3 border border-border">
                {spaces.map((space, idx) => {
                  const title      = getTagValue(space.tags, 'title') ?? 'Untitled';
                  const description = getTagValue(space.tags, 'description') ?? '';
                  const icon       = getTagValue(space.tags, 'icon') ?? '📖';
                  const dTag       = getTagValue(space.tags, 'd') ?? '';
                  const spaceSlug  = dTag.replace('stele:space:', '');
                  const pageCount  = parsePageTags(space.tags).length;
                  const npub       = nip19.npubEncode(space.pubkey);

                  return (
                    <div
                      key={space.id}
                      className={cn(
                        'group relative bg-card p-6 cursor-pointer transition-colors hover:bg-accent',
                      )}
                      onClick={() => navigate(`/s/${spaceSlug}`)}
                    >
                      {/* Index label */}
                      <span className="text-xs font-mono text-muted-foreground/50 mb-4 block">
                        {String(idx + 1).padStart(2, '0')}
                      </span>

                      <div className="flex items-start justify-between gap-3 mb-3">
                        <h3 className="font-semibold text-base tracking-tight leading-snug">
                          {title}
                        </h3>
                        <span className="text-lg shrink-0">{icon}</span>
                      </div>

                      {description && (
                        <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-2">
                          {description}
                        </p>
                      )}

                      <div className="flex items-center justify-between text-xs text-muted-foreground mt-4">
                        <span className="flex items-center gap-1.5 font-mono">
                          <FileText className="h-3 w-3" />
                          {pageCount}p
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/r/${npub}/${spaceSlug}`);
                          }}
                          className="flex items-center gap-1 hover:text-foreground transition-colors"
                        >
                          Read
                          <ArrowUpRight className="h-3 w-3" />
                        </button>
                      </div>

                      {/* Hover arrow */}
                      <ArrowUpRight className="absolute top-4 right-4 h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  );
                })}
              </div>

            ) : (
              /* Empty state */
              <div className="border border-border border-dashed p-16 text-center">
                <p className="text-xs font-mono tracking-[0.15em] text-muted-foreground uppercase mb-3">
                  No spaces yet
                </p>
                <p className="text-muted-foreground text-sm max-w-xs mx-auto mb-8">
                  Create your first documentation space to get started.
                </p>
                <Button
                  onClick={() => setCreateDialogOpen(true)}
                  variant="outline"
                  size="sm"
                  className="gap-2 font-mono text-xs tracking-widest uppercase"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Create space
                </Button>
              </div>
            )}
          </div>
        )}
      </main>

      <CreateSpaceDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />
    </div>
  );
};

export default Index;
